import React, { useState, useMemo, useEffect } from "react";
import TransferConfirmation from "./ui/TransferConfirmation";
import { Button, message, Modal, Spin } from "antd";
import { useSelector } from "react-redux";
import TransferItemList from "./itemList/TransferItemList";
import DestinationList from "./destination/DestinationList";
import {
  buildDestinations,
  filterCurrentDestination,
  buildRemainingItemCountMap,
  buildRemainingPriceBreakup,
  buildNewDestinationInvoicePayload,
  buildTransferItemCountMap,
  buildTransferOrderPayload,
  buildSourceOrderPayload,
  buildSourceInvoicePayload,
  buildTransferPriceBreakup,
  buildUpdatedDestinationInvoice,
  calculateInvoiceTotals,
  findDestinationInvoice,
  getTransferItems,
  validateTransfer,
} from "./utils";
import useAllPosFunctions from "@/hooks/posCustomHook/useAllPosFunctions";
import { TranslateText } from "@/utils/translation";
const t = (textKey) => <TranslateText textKey={textKey} namespace="pos" />;
const TransferModal = ({ open, onClose, onSuccess, items, currentTable }) => {
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [selectedDestination, setSelectedDestination] = useState(null);
  const { selectedOrderItem, tableOrder } = useSelector((store) => store.pos);
  const { hotelDetails } = useSelector((store) => store.login);
  const { invoiceData, data, invoicePostData } = useSelector(
    (store) => store.order,
  );
  const [loading, setLoading] = useState(false);
  const { handleTransferOrder } = useAllPosFunctions();

  const {
    response: posData,
    activePosSlug,
    activePosId,
    activePosType,
  } = useSelector((store) => store.posSetting);

  const { allTables, allRooms, loadingTableData, loadingAllRoomData } =
    useSelector((store) => store.pos);

  const tableOrderDetails = useSelector((state) => state.pos.tableOrder);

  const destinations = useMemo(() => {
    const allDestinations = buildDestinations(allTables, allRooms, invoiceData);

    return filterCurrentDestination({
      destinations: allDestinations,
      invoice: invoicePostData,
    });
  }, [allTables, allRooms, invoiceData, invoicePostData]);

  const handleQuantityChange = (itemId, quantity) => {
    setSelectedQuantities((prev) => ({
      ...prev,
      [itemId]: quantity,
    }));
  };

  const resetTransferState = () => {
    setSelectedQuantities({});
    setSelectedDestination(null);
  };

  useEffect(() => {
    if (open) {
      resetTransferState();
    }
  }, [open]);

  const transferItems = useMemo(
    () => getTransferItems(selectedQuantities),
    [selectedQuantities],
  );

  const transferItemsWithName = transferItems.map((transferItem) => {
    const item = items.find((i) => i.id === transferItem.itemId);

    return {
      ...transferItem,
      name: item?.name,
    };
  });

  const handleTransfer = async () => {
    // 1. Selected items
    const transferItems = getTransferItems(selectedQuantities);

    // 2. Validation
    const validation = validateTransfer({
      destination: selectedDestination,
      transferItems,
    });

    if (!validation.valid) {
      message.warning(validation.message);
      return;
    }

    // 3. Destination invoice
    const destinationInvoice = findDestinationInvoice({
      invoices: invoiceData,
      destination: selectedDestination,
    });

    // 4. Split current order
    const transferItemCountMap = buildTransferItemCountMap({
      itemCountMap: selectedOrderItem.itemCountMap,
      transferItems,
    });

    const remainingItemCountMap = buildRemainingItemCountMap({
      itemCountMap: selectedOrderItem.itemCountMap,
      transferItems,
    });

    // 5. Split invoice
    const transferPriceBreakup = buildTransferPriceBreakup({
      priceBreakup: invoicePostData.priceBreakup,
      transferItems,
    });

    const remainingPriceBreakup = buildRemainingPriceBreakup({
      priceBreakup: invoicePostData.priceBreakup,
      transferItems,
    });

    // 6. Totals
    const transferTotals = calculateInvoiceTotals(transferPriceBreakup);
    const remainingTotals = calculateInvoiceTotals(remainingPriceBreakup);

    /**
     * Destination payloads
     */

    const destinationOrderPayload = buildTransferOrderPayload({
      hotelId: hotelDetails.id,
      selectedDestination,
      transferItemCountMap,
      destinationInvoice,
      invoice: invoicePostData,
      posId: activePosId,
      sourceOrderId: selectedOrderItem.id,
    });

    const destinationInvoicePayload = destinationInvoice
      ? buildUpdatedDestinationInvoice({
          invoice: destinationInvoice,
          transferPriceBreakup,
        })
      : buildNewDestinationInvoicePayload({
          hotelId: hotelDetails.id,
          selectedDestination,
          transferPriceBreakup,
          destinationInvoice,
          invoice: invoicePostData,
          posId: activePosId,
          totals: transferTotals,
        });

    /**
     * Source payloads
     */

    const sourceOrderPayload = buildSourceOrderPayload({
      orderId: selectedOrderItem.id,
      remainingItemCountMap,
      status: selectedOrderItem.status,
    });
    const sourceInvoicePayload = buildSourceInvoicePayload({
      invoice: invoicePostData,
      remainingPriceBreakup,
      totals: remainingTotals,
    });
    try {
      setLoading(true);
      await handleTransferOrder({
        destinationInvoice,
        destinationOrderPayload,
        destinationInvoicePayload,
        sourceOrderPayload,
        sourceInvoicePayload,
        transferApi: true,
      });
      message.success(t("items_transferred_successfully"));
      resetTransferState();
      onClose();
      onSuccess();
    } catch (error) {
      console.error(error);
      message.error(t("unable_to_transfer_items"));
    } finally {
      setLoading(false);
    }
  };

  const isTransferDisabled =
    loading || !selectedDestination || transferItems.length === 0;
  return (
    <Modal
      width={1000}
      open={open}
      maskClosable={!loading}
      closable={!loading}
      keyboard={!loading}
      onCancel={!loading ? onClose : undefined}
      title={t("transfer_items")}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          {t("cancel")}
        </Button>,
        <>
          <TransferConfirmation
            source={{
              type: tableOrderDetails.type,
              name: tableOrderDetails.tableNumber,
            }}
            destination={selectedDestination}
            transferItems={transferItemsWithName}
            onConfirm={handleTransfer}
            loading={loading}
          >
            <Button
              key="transfer"
              type="primary"
              loading={loading}
              disabled={isTransferDisabled}
            >
              {t("transfer")}
            </Button>
          </TransferConfirmation>
        </>,
      ]}
    >
      <Spin spinning={loading} tip={t("transferring_items")}>
        <div className="grid grid-cols-2 gap-6 h-[500px]">
          <TransferItemList
            items={items}
            selectedQuantities={selectedQuantities}
            onQuantityChange={handleQuantityChange}
          />

          <DestinationList
            destinations={destinations}
            loading={loadingTableData || loadingAllRoomData}
            selectedDestination={selectedDestination}
            onDestinationSelect={setSelectedDestination}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default TransferModal;
